export type DatasetColors = {
  color: string
  color0: string
  color1: string
}

export type ChartTheme = {
  fontSize: number
  fontFamily: string
  /**
   * A separate family, not a weight: React Native does not synthesise one, so
   * `fontWeight` on a custom family silently renders regular.
   */
  boldFontFamily: string
  gridTop: number
  titleColor: string
  axis: {
    labelColor: string
    lineColor: string
    splitLineColor: string
    pointerColor: string
  }
  legend: {
    textColor: string
  }
  tooltip: {
    backgroundColor: string
    textColor: string
    shadowColor: string
  }
  selection: {
    /** Behind the column of the year the reader has open. */
    bandColor: string
  }
  baseline: {
    /** The line along the top of the plan's own stack, where both are drawn. */
    lineColor: string
  }
  markPoint: {
    /** Behind a change for the better, and behind one for the worse. */
    gainBackgroundColor: string
    lossBackgroundColor: string
    /** Heavier than the chart's own bold, where the platform has a family. */
    fontFamily: string
    /** Larger than the axis: it is the figure the reader came for. */
    fontSize: number
  }
  markLine: {
    lineColor: string
    labelColor: string
    dangerLineColor: string
    dangerLabelColor: string
    mergedLineColor: string
    mergedLabelColor: string
    /** Behind the label, which is drawn as a chip the reader can press. */
    labelBackgroundColor: string
  }
  dataset: {
    networth: DatasetColors
    liquidAssets: DatasetColors
    income: DatasetColors
    taxes: DatasetColors
    expenses: DatasetColors
    contributions: DatasetColors
  }
}

const buildDatasetColors = (
  hex: string,
  r: number,
  g: number,
  b: number
): DatasetColors => ({
  color: hex,
  color0: `rgba(${r}, ${g}, ${b}, 0.14)`,
  color1: `rgba(${r}, ${g}, ${b}, 0.0)`,
})

export const defaultChartTheme: ChartTheme = {
  fontSize: 11,
  fontFamily: 'Geist',
  boldFontFamily: 'Geist',
  gridTop: 32,
  titleColor: '#475569', // slate-600
  axis: {
    labelColor: '#94a3b8', // slate-400
    lineColor: 'rgba(148, 163, 184, 0.18)',
    splitLineColor: 'rgba(148, 163, 184, 0.07)',
    pointerColor: 'rgba(148, 163, 184, 0.35)',
  },
  legend: {
    textColor: '#64748b', // slate-500
  },
  tooltip: {
    backgroundColor: 'rgba(15, 23, 42, 0.92)', // slate-950
    textColor: '#f1f5f9', // slate-100
    shadowColor: 'rgba(0, 0, 0, 0.2)',
  },
  // Faint: a place on the chart, not a figure on it.
  selection: { bandColor: 'rgba(0, 182, 61, 0.12)' },
  // A colour no bar under it uses: the taxes grey is slate-400, and a line in
  // it would read as one more tax year rather than as the old total.
  baseline: { lineColor: '#475569' },
  // A tint rather than a colour: the figure is already the line's own, and the
  // ground only has to say which way it went.
  markPoint: {
    gainBackgroundColor: 'rgba(0, 182, 61, 0.05)', // the brand accent
    lossBackgroundColor: 'rgba(190, 96, 84, 0.05)',
    fontFamily: 'Geist',
    fontSize: 14,
  },
  /*
   * A reference mark, not a data series: neutral and quiet, so it never
   * out-shouts the line it annotates. Only danger keeps a hue.
   */
  markLine: {
    lineColor: 'rgba(100, 116, 139, 0.45)', // slate-500
    labelColor: '#64748b',
    dangerLineColor: 'rgba(190, 96, 84, 0.65)',
    dangerLabelColor: '#be6054',
    mergedLineColor: 'rgba(71, 85, 105, 0.55)', // slate-600
    mergedLabelColor: '#475569',
    // Opaque, which the chip's bottom rule depends on: the rule is a hard
    // shadow of the chip itself, and a translucent fill lets it through as a
    // tint over the whole thing.
    labelBackgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  /*
   * One accent and neutrals, not six hues.
   *
   * The lead series in each chart carries the brand green; everything beside it
   * is a grey at a different weight. Colour then means "this is the number you
   * came for", instead of meaning "this is a different row in the legend".
   *
   * Expenses keeps a hue because it is the one category where colour carries
   * meaning, and it is muted so it reads as a caution rather than an alarm.
   */
  dataset: {
    networth: buildDatasetColors('#00b63d', 0, 182, 61), // brand accent
    liquidAssets: buildDatasetColors('#64748b', 100, 116, 139), // slate-500
    income: buildDatasetColors('#00b63d', 0, 182, 61), // brand accent
    taxes: buildDatasetColors('#94a3b8', 148, 163, 184), // slate-400
    expenses: buildDatasetColors('#be6054', 190, 96, 84), // muted terracotta
    contributions: buildDatasetColors('#a1887f', 161, 136, 127), // muted clay
  },
}

export const mobileChartTheme: ChartTheme = {
  ...defaultChartTheme,
  fontFamily: 'Geist-Regular',
  boldFontFamily: 'Geist-SemiBold',
  markPoint: {
    ...defaultChartTheme.markPoint,
    // React Native does not synthesise a weight, so the heavier family is named.
    fontFamily: 'Geist-Bold',
  },
  gridTop: 30,
}
